// The versioned, once-per-browser `localStorage` persistence adapter for the
// first-run guidance "seen" flag (onboarding design D-Persist). Waggle has no
// account/identity model (DESIGN §15 defers accounts), so first-run acknowledgement
// is scoped once per browser/origin under a *new, independent* `localStorage` key —
// deliberately separate from the design-locked `waggle-session` `sessionStorage`
// record (wrong scope: per-match, tab-only; wrong lifetime: cleared on browser
// close; and its validator is locked to `{token,matchId,role,phase,isHost}`).
//
// The record is a single `{ seenVersion: number }`. `hasSeenGuide()` returns true
// ONLY when a well-formed record's `seenVersion === GUIDE_VERSION`; an absent, older,
// or malformed record is treated as **not seen** (fail-open to showing the aid), so a
// materially-changed guide re-presents to every browser by bumping `GUIDE_VERSION`
// with no ad-hoc clearing. Every storage access is try/catch-guarded — mirroring
// `session-store.ts`'s `createSessionStorageStore` — so a private-mode / quota /
// sandboxed-`SecurityError` failure degrades to "not seen" (read) or a silent no-op
// (write) rather than crashing boot. The adapter is generic over an injected
// `Storage`-like shim: production passes `window.localStorage` (from `main.tsx`),
// tests pass an in-memory / throwing double.

/** The `localStorage` key holding the first-run acknowledgement record — independent
 * of `waggle-session` (design D-Persist). */
export const ONBOARDING_SEEN_KEY = 'waggle-onboarding-seen';

/** The rounds-mode counterpart's key (WAG-11): the Sandbox screen's guide is
 * different copy from the live Colony guide (DESIGN §2/§6 — no hot-swap, a
 * differently-placed prompt bar), so dismissing one must not dismiss the other. A
 * separate KEY, not a separate version: `GUIDE_VERSION` still gates both records, so
 * a future copy change re-presents both guides together unless a change is scoped to
 * one mode only. */
export const ROUNDS_ONBOARDING_SEEN_KEY = 'waggle-onboarding-seen-rounds';

/** The current guide version. A stored `seenVersion` equal to this counts as "seen";
 * bumping it re-presents a materially-changed guide to every browser (design
 * D-Persist / Migration Plan). Starts at 1. */
export const GUIDE_VERSION = 1;

/** The subset of the DOM `Storage` API this adapter needs — accepted as a parameter
 * so `main.tsx` passes a `window.localStorage` shim and tests pass an in-memory
 * double (structurally identical to `session-store.ts`'s `SessionStorageLike`). */
export type OnboardingStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The guarded first-run "seen" adapter (design D-Persist). Both methods swallow any
 * storage error: `hasSeenGuide()` reports `false` (fail-open to showing the guide)
 * and `markGuideSeen()` no-ops. */
export interface OnboardingSeenStore {
  /** `true` only when a well-formed stored record has `seenVersion === GUIDE_VERSION`;
   * an absent, older, malformed, or unreadable record ⇒ `false` (not seen). */
  hasSeenGuide(): boolean;
  /** Persists `{ seenVersion: GUIDE_VERSION }`; silently no-ops if storage throws. */
  markGuideSeen(): void;
}

/** A stored record is "seen" only if it is a plain object carrying a numeric
 * `seenVersion` exactly equal to the current `GUIDE_VERSION`. Anything else — `null`,
 * an array, a missing/wrong-typed field, or an older/newer version — is not seen. */
function recordIsSeen(parsed: unknown): boolean {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return false;
  }
  const seenVersion = (parsed as Record<string, unknown>).seenVersion;
  return typeof seenVersion === 'number' && seenVersion === GUIDE_VERSION;
}

/**
 * Builds an {@link OnboardingSeenStore} over a `Storage`-like backing, reading/writing
 * the `{ seenVersion }` record under `key` (default {@link ONBOARDING_SEEN_KEY}).
 * Every access is guarded: a throwing `getItem` (private mode, `SecurityError`) makes
 * `hasSeenGuide()` return `false`; malformed JSON or a shape/version mismatch is
 * likewise "not seen"; a throwing `setItem` (quota) makes `markGuideSeen()` a silent
 * no-op. Nothing here ever propagates a storage exception (design D-Persist).
 */
export function createOnboardingSeenStore(
  storage: OnboardingStorageLike,
  key: string = ONBOARDING_SEEN_KEY,
): OnboardingSeenStore {
  return {
    hasSeenGuide(): boolean {
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return false; // storage unavailable — fail open to showing the guide
      }
      if (raw === null) {
        return false; // no record — not seen
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return false; // malformed JSON — not seen
      }
      return recordIsSeen(parsed);
    },
    markGuideSeen(): void {
      try {
        storage.setItem(key, JSON.stringify({ seenVersion: GUIDE_VERSION }));
      } catch {
        // Storage disabled/full: the acknowledgement just does not persist; the guide
        // may re-show on the next boot, which is the safe degrade.
      }
    },
  };
}
